/* Complement recommendations — product page and cart.
 *
 * Shopify remains the authority on price, stock and what the cart charges.
 * This script asks app.dyravinir.is which reviewed pack survived the
 * suitability gates, renders it, and adds it with one request. It never
 * computes a discount, never quotes a delivery threshold it was not handed by
 * Liquid, and treats every failure as "render nothing" rather than as an error
 * the visitor has to read.
 *
 * The core is exported for tests (tests/js/complements.test.cjs) and runs
 * under node's vm with no DOM, the same arrangement as dv-owned-bundles.js.
 */
(function (scope) {
  'use strict';

  var API_ORIGIN_DEFAULT = 'https://app.dyravinir.is';

  /* The origin is per-element so the local preview harness
   * (tools/preview_complements.py) can point the same file at 127.0.0.1
   * without a second copy of it drifting from the one the theme ships. Only
   * ever set by us, in Liquid; it is never read from the URL. */
  function apiOrigin(root) {
    /* Presence decides, not truthiness: an empty `data-api-base` means
     * same-origin, which is what the local harness needs. Falling back on
     * falsiness would silently send a preview's requests to production. */
    if (root && root.hasAttribute && root.hasAttribute('data-api-base')) {
      return root.getAttribute('data-api-base');
    }
    return API_ORIGIN_DEFAULT;
  }

  /* ISK has no minor unit on the storefront and the theme prints thousands
   * with a dot ("18.595 kr."). Intl would give a non-breaking space and a
   * currency code, which does not match the surrounding page. */
  function money(isk) {
    return String(Math.round(isk)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' kr.';
  }

  /* Icelandic counts inflect: "1 dós" but "4 dósir". The pack supplies both
   * forms, and the rule that matters here is that any number ending in 1 but
   * not 11 takes the singular ("21 dós"). */
  function plural(count, singular, many) {
    var last = Math.abs(count) % 10;
    var teen = Math.abs(count) % 100;
    return count && last === 1 && teen !== 11 ? singular : many;
  }

  function optionLabel(option, copy) {
    var unit = plural(option.quantity, copy.unit_singular, copy.unit_plural);
    return option.quantity + ' ' + unit;
  }

  /* The CTA is a template because the quantity is derived per SKU, not fixed
   * per pack: the same block can offer two cans of one recipe and one bag of a
   * treat. A literal "Setja fjórar dósir í körfu" would be wrong the moment the
   * ladder picked a different step. A template with no placeholders is left
   * exactly as written, which is how the second-bag pack keeps its own wording. */
  function formatCta(pack, option) {
    var unit = plural(option.quantity, pack.unit_singular, pack.unit_plural);
    return String(pack.cta)
      .replace('{count}', option.quantity)
      .replace('{unit}', unit);
  }

  /** Pick the pack to show. One block, one decision — never two competing
   *  asks on the same screen. The API already returns them best-first. */
  function choosePack(payload) {
    if (!payload || payload.status !== 'ok') return null;
    var packs = payload.packs || [];
    for (var i = 0; i < packs.length; i += 1) {
      if (packs[i].options && packs[i].options.length) return packs[i];
    }
    return null;
  }

  /** Verify the add against the cart Shopify actually holds.
   *
   *  A 200 from /cart/add.js is not proof: the request can time out after
   *  Shopify accepted it, and a retry would then double the line. So the cart
   *  is read back and the quantity compared, and an uncertain result is
   *  reported as uncertain rather than retried.
   */
  function verify(before, after, variantId, added) {
    var count = function (cart) {
      return (cart.items || []).reduce(function (sum, line) {
        return String(line.variant_id) === String(variantId) ? sum + line.quantity : sum;
      }, 0);
    };
    var delta = count(after) - count(before);
    return { delta: delta, complete: delta >= added };
  }

  async function addPack(context) {
    var option = context.option;
    var before = await context.getCart();
    if (before.currency && before.currency !== 'ISK') throw new Error('currency');
    try {
      await context.addItems([{ id: option.variant_id, quantity: option.quantity }]);
    } catch (error) {
      /* A timeout may still have been accepted. The read below decides. */
      if (error && error.soldOut) throw error;
    }
    var after = await context.getCart();
    var result = verify(before, after, option.variant_id, option.quantity);
    if (!result.complete) throw new Error('uncertain');
    return result;
  }

  /* Export the core for the node tests only. Their vm sandbox supplies `module`;
   * a browser page must not get one. Creating a global `module` here made UMD
   * bundles on the page, the Judge.me widget among them, take their CommonJS
   * branch and throw ("Cannot read properties of undefined (reading 'fn')"). */
  var exportTarget = typeof document === 'undefined' && scope.module ? scope.module : {};
  exportTarget.exports = {
    money: money,
    plural: plural,
    optionLabel: optionLabel,
    formatCta: formatCta,
    choosePack: choosePack,
    verify: verify,
    addPack: addPack,
  };

  if (typeof document === 'undefined') return;

  var TEXT = {
    added: 'Bætt í körfuna.',
    soldOut: 'Þessi vara seldist upp á meðan. Uppfærðu síðuna til að sjá það sem er til.',
    uncertain: 'Við náðum ekki að staðfesta körfuna. Opnaðu körfuna til að sjá stöðuna.',
    working: 'Augnablik.',
    viewCart: 'Skoða körfu',
  };

  function track(event, pack, option, root) {
    /* First-party funnel beacon, same endpoint and vocabulary the advisor
     * uses. `cross_sell_impression` / `cross_sell_click` already exist in the
     * accepted event names and meta keys, so this needs no schema change and
     * lands in the same advisor_events table the rest of the funnel reports
     * from. `surface` separates the storefront block from the advisor's own. */
    try {
      var body = JSON.stringify({
        event: event,
        cross_sell_id: pack.pack_id,
        lang: 'is',
        meta: {
          surface: root.dataset.surface === 'cart' ? 'cart' : 'pdp',
          placement: 'complement_pack',
          product_handle: option ? String(option.handle).slice(0, 200) : '',
          species: root.dataset.species || '',
        },
      });
      var url = apiOrigin(root) + '/api/advisor/dogs/event';
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
    } catch (error) {
      /* Analytics must never break merchandising. */
    }
  }

  function render(root, payload) {
    var pack = choosePack(payload);
    var options = root.querySelector('[data-dv-options]');
    var status = root.querySelector('[data-dv-status]');
    options.textContent = '';
    status.textContent = '';
    if (!pack) {
      root.hidden = true;
      return;
    }
    root.querySelector('[data-dv-heading]').textContent = pack.heading;
    root.querySelector('[data-dv-body]').textContent = pack.body;

    pack.options.forEach(function (option, index) {
      var item = document.createElement('li');
      item.className = 'dv-complements__option';

      if (option.image_url) {
        var image = document.createElement('img');
        image.className = 'dv-complements__thumb';
        image.src = option.image_url + (option.image_url.indexOf('?') > -1 ? '&' : '?') + 'width=160';
        /* Decorative: the product name sits right beside it, so alt text here
         * would make a screen reader announce the same thing twice. */
        image.alt = '';
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        /* A dead CDN URL must collapse the column rather than leave an empty
         * grey square where a product photo should be. */
        image.addEventListener('error', function () {
          image.remove();
          item.classList.add('dv-complements__option--noimage');
        });
        item.appendChild(image);
      } else {
        item.classList.add('dv-complements__option--noimage');
      }

      var text = document.createElement('div');
      var name = document.createElement('p');
      name.className = 'dv-complements__name';
      name.textContent = option.title;
      var price = document.createElement('p');
      price.className = 'dv-complements__price';
      price.textContent = optionLabel(option, pack) + ' — ';
      var strong = document.createElement('strong');
      strong.textContent = money(option.total_price_isk);
      price.appendChild(strong);
      text.appendChild(name);
      text.appendChild(price);
      item.appendChild(text);

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'dv-complements__add';
      /* One purchase action per row, and only the first row leads: the rest
       * are alternatives to it, not competing asks. */
      button.textContent = index === 0 ? formatCta(pack, option) : 'Velja þessa';
      button.addEventListener('click', function () {
        addFromButton(root, pack, option, button, status);
      });
      item.appendChild(button);
      options.appendChild(item);
    });

    root.hidden = false;
    track('cross_sell_impression', pack, pack.options[0], root);
  }

  async function addFromButton(root, pack, option, button, status) {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = TEXT.working;
    track('cross_sell_click', pack, option, root);
    try {
      await addPack({
        option: option,
        getCart: function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
        },
        addItems: function (items) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          }).then(function (response) {
            if (response.ok) return response.json();
            /* 422 is Shopify saying the quantity is not available — the one
             * failure the customer can act on, so it gets its own message
             * instead of the generic one. */
            var error = new Error('add-failed');
            error.soldOut = response.status === 422;
            throw error;
          });
        },
      });
      status.textContent = TEXT.added + ' ';
      var link = document.createElement('a');
      link.href = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart' : '/cart';
      link.textContent = TEXT.viewCart;
      status.appendChild(link);
      document.documentElement.dispatchEvent(new CustomEvent('dv:complement-added', { bubbles: true, detail: { pack: pack.pack_id, sku: option.sku } }));
      if (root.dataset.surface === 'cart') window.location.reload();
    } catch (error) {
      status.textContent = error && (error.soldOut || error.message === 'sold-out') ? TEXT.soldOut : TEXT.uncertain;
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  var requestSeq = 0;

  function load(root) {
    var sku = root.dataset.anchorSku;
    if (!sku || root.dataset.loadedSku === sku) return;
    /* Hide before asking. The gates are variant-specific — a chicken-free
     * 12,2 kg bag and its chicken-containing 2 kg sibling get different
     * answers — so leaving the previous variant's recommendation on screen
     * during the fetch would briefly show one the new variant was refused. */
    if (root.dataset.loadedSku) root.hidden = true;
    root.dataset.loadedSku = sku;

    /* Responses can arrive out of order when a visitor clicks through
     * variants quickly; only the newest one may render. */
    requestSeq += 1;
    var seq = requestSeq;

    var url = apiOrigin(root) + '/api/recommendations'
      + '?anchor_sku=' + encodeURIComponent(sku)
      + '&surface=' + encodeURIComponent(root.dataset.surface || 'pdp')
      + '&cart_skus=' + encodeURIComponent(root.dataset.cartSkus || '');
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (payload) {
        if (seq !== requestSeq) return;
        render(root, payload);
      })
      .catch(function () {
        if (seq === requestSeq) root.hidden = true;
      });
  }

  function init(scopeNode) {
    (scopeNode || document).querySelectorAll('[data-dv-complements]').forEach(load);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Dawn re-renders the product and cart sections through the Section
   * Rendering API, which replaces this element and its data-anchor-sku. Both
   * hooks below re-read it: the section event covers a variant change on the
   * product page, and the cart change event covers a quantity edit. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('change', function (event) {
    if (event.target && event.target.closest && event.target.closest('variant-selects, variant-radios, .product-form')) {
      window.setTimeout(function () { init(); }, 0);
    }
  });
})(typeof globalThis !== 'undefined' ? globalThis : this);
